import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Heading from '@deliveroo/consumer-component-library/components/Heading';

import i18n from '../../utils/i18n';

import { getOrderStatus, getOrderPunchCard } from '../../actions/orderActions';
import { trackViewedOrderStatusPage } from '../../actions/tracking/orderStatus';
import { trackViewedSharedOrderPage } from '../../actions/tracking/orderStatusSharing';

import Header from '../Header';
import InfoBannerContainer from '../InfoBanner';
import OrderStatusMapWrapper from '../OrderStatusMapWrapper';
import BottomSlant from '../../components/BottomSlant';
import OrderStatusProgressContainer from '../OrderStatusProgress';
import OrderDetailsColumnContainer from '../OrderDetailsColumn';
import OrderStatusMissedCallModalContainer from '../OrderStatusMissedCallModal';
import PunchCardContainer from '../PunchCard';
import OrderStatusPolling from '../../utils/orderStatusPolling';

import {
  getOrderStatus as getOrderStatusState,
  getShouldShowInfoBanner,
} from '../../selectors/orderStatusSelectors';
import { getCurrentQuery } from '../../selectors/requestSelectors';
import { getOrderPunchCard as getOrderPunchCardState } from '../../selectors/orderPunchCardsSelectors';
import { whichVariant } from '../../selectors/configSelectors';
import { MVT_POE_12 } from '../../flags/experiments';

import getSafe from '../../utils/getSafe';

import styles from './styles.scss';

const NORMAL_POLLING_INTERVAL_MS = 10000;
const TIMES_POLLED_LIMIT = 3;
const PUNCH_CARD_POLLED_LIMIT = 3;

// eslint-disable-next-line react/no-deprecated
export class OrderStatusContainer extends Component {
  state = {
    safeZoneEl: undefined,
    pageContentEl: undefined,
    punchCardPollCount: 0,
  };

  pollAction = () => {
    const { isPunchCardVariant, orderId, actions, failedAttempts } = this.props;

    actions.getOrderStatus(orderId, failedAttempts);

    if (isPunchCardVariant && this.state.punchCardPollCount < PUNCH_CARD_POLLED_LIMIT) {
      actions.getOrderPunchCard(orderId);
      this.setState((prevState) => ({
        punchCardPollCount: prevState.punchCardPollCount + 1,
      }));
    }
  };

  componentWillMount() {
    this.pollAction();
  }

  componentDidMount() {
    if (!this.polling) {
      this.polling = new OrderStatusPolling({
        pollAction: this.pollAction,
        normalPollingIntervalMs: NORMAL_POLLING_INTERVAL_MS,
        timesPolledLimit: TIMES_POLLED_LIMIT,
      });
    }

    if (this.safeZoneEl && this.pageContentEl) {
      this.setState({
        safeZoneEl: this.safeZoneEl,
        pageContentEl: this.pageContentEl,
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      status: { attributes, error, isSharedOrder, isCompletedStatus, isFailedStatus },
      actions: {
        trackViewedOrderStatusPage: dispatchTrackViewedOrderStatusPage,
        trackViewedSharedOrderPage: dispatchTrackViewedSharedOrderPage,
      },
    } = this.props;

    const currentAnalyticsStatusMessage =
      attributes && attributes.analytics && attributes.analytics.status_message;
    const newAnalyticsStatusMessage =
      nextProps.status &&
      nextProps.status.attributes &&
      nextProps.status.attributes.analytics &&
      nextProps.status.attributes.analytics.status_message;

    /* if the status has changed send a viewed status tracking event */
    if (
      currentAnalyticsStatusMessage &&
      newAnalyticsStatusMessage &&
      currentAnalyticsStatusMessage !== newAnalyticsStatusMessage
    ) {
      if (isSharedOrder) {
        dispatchTrackViewedSharedOrderPage();
      } else {
        dispatchTrackViewedOrderStatusPage(false);
      }
    }

    if (this.props.status !== nextProps.status && this.polling) {
      const shouldPoll = !isCompletedStatus && !isFailedStatus;
      const shouldResetPoll = !(error && error.statusCode);

      if (shouldPoll) {
        this.polling.onUpdate({
          error,
          shouldResetPoll,
        });
      }
    }
  }

  componentDidUpdate() {
    if (!this.state.safeZoneEl && this.safeZoneEl) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        safeZoneEl: this.safeZoneEl,
      });
    }

    if (!this.state.pageContentEl && this.pageContentEl) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        pageContentEl: this.pageContentEl,
      });
    }
  }

  componentWillUnmount() {
    if (this.polling) {
      this.polling.unmount();
    }
  }

  render() {
    const {
      showMap,
      shouldShowInfoBanner,
      shouldShowPunchCard,
      status: { attributes, order, isSharedOrder },
    } = this.props;

    const classNames = classnames(styles['order-status'], {
      [styles['map-visible']]: showMap,
    });

    const isPollingAndUnderPollLimit = this.polling && this.polling.isPollingAndUnderPollLimit();
    const isOverPollLimit = this.polling && this.polling.isOverPollLimit();

    return (
      <div className={classNames}>
        <Header isTransparent logoColor="white" />
        <OrderStatusMapWrapper safeZoneEl={this.safeZoneEl} pageContentEl={this.pageContentEl}>
          <div className={styles.slant}>
            <BottomSlant colorTheme="teal" />
          </div>

          {isSharedOrder && (
            <div className={styles['order-owner']}>
              <Heading spacing="0 medium small" color="white" level={4}>
                {i18n.gettext('order_status_page.shared_status_header.text', {
                  customer_name: order.orderOwnerName,
                })}
              </Heading>
            </div>
          )}

          {!isSharedOrder && <OrderStatusMissedCallModalContainer />}

          <div
            className={styles.content}
            ref={(pageContentEl) => {
              this.pageContentEl = pageContentEl;
            }}
          >
            <div className={styles.primary}>
              <div className={styles['primary-content']}>
                {attributes &&
                  order && (
                    <div className={styles['order-status-progress']}>
                      <OrderStatusProgressContainer
                        isPollingAndUnderPollLimit={isPollingAndUnderPollLimit}
                        isOverPollLimit={isOverPollLimit}
                      />

                      <div className={styles['hide-on-mobile']}>
                        {shouldShowInfoBanner && <InfoBannerContainer spacing="medium 0 0 0" />}

                        {shouldShowPunchCard && <PunchCardContainer spacing="medium 0 0 0" />}
                      </div>
                    </div>
                  )}
              </div>

              {showMap && (
                <div
                  className={styles['map-safezone']}
                  ref={(safeZoneEl) => {
                    this.safeZoneEl = safeZoneEl;
                  }}
                />
              )}
            </div>
            <div className={styles.secondary}>
              <OrderDetailsColumnContainer />
            </div>
          </div>
        </OrderStatusMapWrapper>
      </div>
    );
  }
}

OrderStatusContainer.defaultProps = {
  showMap: false,
  isPunchCardVariant: false,
  shouldShowInfoBanner: true,
  shouldShowPunchCard: false,
  failedAttempts: 0,
};

OrderStatusContainer.propTypes = {
  orderId: PropTypes.string.isRequired,
  showMap: PropTypes.bool,
  /**
   * Whether the user is in the punchCard experiment
   */
  isPunchCardVariant: PropTypes.bool,
  /**
   * Whether to show the info banner
   */
  shouldShowInfoBanner: PropTypes.bool,
  /**
   * Whether to show the punch card
   */
  shouldShowPunchCard: PropTypes.bool,
  /*
   How many times consecutively it's tried to fetch the order status
   */
  failedAttempts: PropTypes.number,
  status: PropTypes.shape({
    order: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    isSharedOrder: PropTypes.bool,
    error: PropTypes.shape({
      statusCode: PropTypes.number,
    }),
    attributes: PropTypes.shape({
      uiStatus: PropTypes.string.isRequired,
      showLiveIndicator: PropTypes.bool.isRequired,
      formattedUpdatedAt: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      etaMessage: PropTypes.string,
      message: PropTypes.string.isRequired,
      progress: PropTypes.object,
      processingSteps: PropTypes.arrayOf(PropTypes.object.isRequired),
      currentProgressPercentage: PropTypes.number,
      analytics: PropTypes.shape({
        status_message: PropTypes.string,
      }),
    }),
  }).isRequired,
  actions: PropTypes.shape({
    getOrderStatus: PropTypes.func.isRequired,
    getOrderPunchCard: PropTypes.func.isRequired,
    trackViewedOrderStatusPage: PropTypes.func.isRequired,
    trackViewedSharedOrderPage: PropTypes.func.isRequired,
  }).isRequired,
};

const mapStateToProps = (state) => {
  const status = getOrderStatusState(state);
  // Get the orderId from the url query so we're not reliant on fetching the order
  const { orderId } = getCurrentQuery(state);
  const punchCardsState = getOrderPunchCardState(orderId)(state);
  const isPunchCardVariant = whichVariant(state, MVT_POE_12) === 'punchcard';

  return {
    orderId,
    status,
    showMap: status.showMap,
    isPunchCardVariant,
    shouldShowInfoBanner: getShouldShowInfoBanner(state),
    shouldShowPunchCard: isPunchCardVariant && getSafe(() => punchCardsState.shouldShow),
    failedAttempts: status.failedAttempts,
  };
};

const mapActionsToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      getOrderStatus,
      getOrderPunchCard,
      trackViewedOrderStatusPage,
      trackViewedSharedOrderPage,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapActionsToProps,
)(OrderStatusContainer);
